import { useEffect, useState, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { DURATION, EASE_OUT, LOOP } from '../tokens/motion'
import { cn } from '../lib/cn'

/**
 * A failure, in the system's own words, with the remedy as a control.
 * Not for empty states: an absence is not a failure and must not look like one.
 */
export function StateNote({
  title,
  detail,
  action,
  className,
}: {
  title: string
  detail?: string
  action?: ReactNode
  className?: string
}) {
  return (
    <div role="alert" className={cn('flex flex-col items-start gap-3 py-4', className)}>
      <div>
        <p className="text-ui text-foreground">{title}</p>
        {detail ? <p className="wf-selectable mt-0.5 text-caption text-muted-foreground">{detail}</p> : null}
      </div>
      {action}
    </div>
  )
}

/** An absence naming itself: "No uploads yet." Quiet, never styled as an error. */
export function EmptyNote({ title, detail, action, className }: { title: string; detail?: string; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-start gap-3 py-6', className)}>
      <div>
        <p className="text-ui text-muted-foreground">{title}</p>
        {detail ? <p className="mt-0.5 text-caption text-muted-foreground/80">{detail}</p> : null}
      </div>
      {action}
    </div>
  )
}

/**
 * Loading is three quiet dots. A read that lands in a few frames draws
 * nothing — the region only announces itself to assistive technology — but
 * one that takes a beat earns the studio's arriving mark: three dots
 * breathing in reading order, the pulse work-in-progress uses everywhere.
 * The delay is what keeps a fast page from ever flashing it.
 */
export function Loading({ label }: { label: string }) {
  const [late, setLate] = useState(false)
  useEffect(() => {
    const timer = window.setTimeout(() => setLate(true), 240)
    return () => window.clearTimeout(timer)
  }, [])
  return (
    <div role="status" aria-live="polite" className="py-1">
      {late ? (
        <motion.span
          className="flex items-center gap-1.5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: DURATION.base, ease: EASE_OUT }}
          aria-hidden
        >
          {[0, 1, 2].map((index) => (
            <motion.span
              key={index}
              className="size-1.5 rounded-full bg-muted-foreground"
              animate={{ opacity: [0.2, 0.9, 0.2] }}
              transition={{ ...LOOP.pulse, delay: index * 0.18 }}
            />
          ))}
        </motion.span>
      ) : null}
      <span className="sr-only">{label}</span>
    </div>
  )
}
